<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div
      class="absolute inset-0 z-40 hidden bg-black/50 md:block"
      @click="$emit('close')"
    ></div>
    <div
      class="relative z-50 h-full w-full bg-white p-4 sm:p-5 md:h-auto md:w-[90vw] md:max-w-sm md:rounded-lg md:shadow-lg"
      @click.stop
    >
      <div class="mb-3 flex items-center justify-between">
        <button
          type="button"
          class="rounded px-3 py-1 text-lg hover:bg-gray-100"
          @click="prevMonth"
        >
          ‹
        </button>
        <div class="text-base font-semibold">{{ monthLabel }}</div>
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="rounded px-3 py-1 text-lg hover:bg-gray-100"
            @click="nextMonth"
          >
            ›
          </button>
          <button
            type="button"
            class="rounded px-3 py-1 text-base hover:bg-gray-100"
            @click="$emit('close')"
          >
            ×
          </button>
        </div>
      </div>

      <div
        class="mb-2 grid grid-cols-7 gap-1 text-center text-xs text-gray-600"
      >
        <div v-for="d in weekDays" :key="d" class="font-medium">{{ d }}</div>
      </div>

      <div class="grid grid-cols-7 gap-1 text-sm">
        <template v-for="cell in monthCells" :key="cell.key">
          <div v-if="!cell.day" class="h-9"></div>
          <button
            v-else
            type="button"
            class="flex h-9 items-center justify-center rounded hover:bg-indigo-50"
            :class="{
              'bg-pink-500 text-white hover:bg-purple-950':
                cell.iso === selectedIso,
            }"
            @click="selectDay(cell.day)"
          >
            {{ cell.day }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { formatLocalDate } from "~/composables/useDateTime";

const props = defineProps<{ initialDate?: string }>();
const emit = defineEmits<{
  (e: "select", iso: string): void;
  (e: "close"): void;
}>();

function parseInitialDate(value?: string): Date | null {
  if (!value) return null;

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const currentMonth = ref(parseInitialDate(props.initialDate) ?? new Date());
const selectedIso = ref(props.initialDate || "");

const monthLabel = computed(() =>
  currentMonth.value.toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  }),
);
const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const monthCells = computed(() => {
  const y = currentMonth.value.getFullYear();
  const m = currentMonth.value.getMonth();
  const first = new Date(y, m, 1);
  const startDay = first.getDay();
  const total = new Date(y, m + 1, 0).getDate();
  const cells: Array<{ key: string; day?: number; iso?: string | null }> = [];
  for (let i = 0; i < startDay; i++) cells.push({ key: `b-${i}` });
  for (let d = 1; d <= total; d++) {
    const dt = new Date(y, m, d);
    cells.push({ key: `d-${d}`, day: d, iso: formatLocalDate(dt) });
  }
  return cells;
});

function prevMonth() {
  currentMonth.value = new Date(
    currentMonth.value.getFullYear(),
    currentMonth.value.getMonth() - 1,
    1,
  );
}
function nextMonth() {
  currentMonth.value = new Date(
    currentMonth.value.getFullYear(),
    currentMonth.value.getMonth() + 1,
    1,
  );
}

function selectDay(day: number | undefined) {
  if (!day) return;
  const y = currentMonth.value.getFullYear();
  const m = currentMonth.value.getMonth();
  const d = new Date(y, m, day);
  const iso = formatLocalDate(d);
  selectedIso.value = iso;
  emit("select", iso);
}

watch(
  () => props.initialDate,
  (v) => {
    selectedIso.value = v || "";
    if (v) {
      const parsed = parseInitialDate(v);
      if (parsed) currentMonth.value = parsed;
    }
  },
);
</script>

<style scoped>
/* Modal styling applied via Tailwind classes */
</style>
